/**
 * SellerDetail — the seller detail sheet body (design revamp).
 *
 * Production translation of `SellerDetail` in
 * `docs/GardEn/handoff-2026-06/garden/screens-shops.jsx`: avatar + name,
 * rating · reviews · years, a 2×2 action grid (call / WhatsApp / 2GIS /
 * ask-assistant), an opening-hours row, and a sample inventory list.
 *
 * Actions:
 *   - Ask-assistant navigates to `/chat` (the only wired action in this
 *     demo slice).
 *   - 2GIS opens the seller's pavilion on 2gis.kg (`geo/{lng},{lat}`) in a new
 *     tab when the finder payload carries coordinates; disabled otherwise.
 *   - Call dials the seller's business number (`tel:`); WhatsApp opens `wa.me`
 *     when the seller has a WhatsApp number, otherwise it's disabled. The
 *     numbers come from the finder payload (SCRUM-134 — a shop's public business
 *     contact, not personal PII).
 *
 * Inventory is fetched lazily (`useSellerInventory`, enabled once a seller is
 * selected) and shows a Skeleton list while loading, an empty line when the
 * seller has no sampled products, and the items (name + formatted price)
 * otherwise. Prices are Decimal-as-number from the browse envelope → formatted
 * with `fmtSom`. Tokens via `--g-*`; strings via the `shops`/`cta` i18n keys.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Avatar, Button, Icon, Skeleton } from "@garden/ui";
import { fmtSom, parseDecimal } from "@/lib/format";
import type { SupportedLanguage } from "@/lib/i18n";
import type { SellerSummary } from "@/lib/garden-api";

import { useSellerInventory } from "@/features/shops/api";
import { sellerInitials } from "@/features/shops/SellerCard";

export interface SellerDetailProps {
  seller: SellerSummary;
  /** Active UI language (drives the сом/som money suffix). */
  lang: SupportedLanguage;
}

/** The chat route the ask-assistant CTA opens. */
export const CHAT_ROUTE = "/chat";

export function SellerDetail({ seller, lang }: SellerDetailProps): ReactElement {
  const { t } = useTranslation("shops");
  const navigate = useNavigate();
  const inventory = useSellerInventory(seller.id);
  const rating = parseDecimal(seller.rating);
  // SCRUM-144: `market_label` has no per-language variant (a pre-composed
  // display string, schema/API change out of scope here) — only the
  // market-name fallback can be localized from the fields already on the wire.
  const marketName =
    lang === "ky" ? (seller.market_name_ky ?? seller.market_name_ru) : seller.market_name_ru;
  const market = seller.market_label ?? marketName;
  const items = inventory.data?.items ?? [];

  // 2GIS deep link to the seller's pavilion (NB: geo/{lng},{lat} order).
  // Enabled only when the finder payload carries coordinates.
  const has2gis = seller.lat !== null && seller.lng !== null;
  const geo2gisUrl = has2gis
    ? `https://2gis.kg/geo/${seller.lng},${seller.lat}`
    : undefined;

  // Seller business contact (SCRUM-134). WhatsApp is optional; tel: opens the
  // dialer and wa.me wants digits only (no leading "+").
  //
  // The phone is NO LONGER always present (v0053). Where two real stalls share
  // one number the importer mints a sentinel for the second claimant to clear
  // the unique phone index, and the API now withholds those — 9 of 79 QA
  // sellers, every one reading as `verified`, each of them offering this Call
  // button bound to a number that reaches nobody. A disabled button is the
  // honest state: the stall is still findable by market and pavilion above.
  const phoneRaw = seller.phone_e164;
  const telUrl =
    phoneRaw !== null && phoneRaw !== undefined && phoneRaw !== ""
      ? `tel:${phoneRaw}`
      : undefined;
  const whatsappRaw = seller.whatsapp_phone_e164;
  const whatsappUrl =
    whatsappRaw !== null && whatsappRaw !== ""
      ? `https://wa.me/${whatsappRaw.replace(/[^\d]/g, "")}`
      : undefined;

  return (
    <div data-testid={`seller-detail-${seller.id}`}>
      {/* Identity */}
      <div className="mb-4 flex gap-3.5">
        <Avatar initials={sellerInitials(seller.business_name)} size={56} />
        <div className="flex-1">
          <div className="text-lg font-bold tracking-[-0.02em] text-foreground">
            {seller.business_name}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <Icon
              name="star"
              size={14}
              className="fill-[var(--g-warn)] text-[var(--g-warn)]"
            />
            <span className="text-[13px] font-bold tabular-nums text-foreground">
              {rating.toFixed(1)}
            </span>
            <span className="text-[12.5px] text-[var(--g-ink-3)]">
              · {t("reviews", { count: seller.reviews_count })}
              {seller.years_in_business != null
                ? ` · ${t("years", { count: seller.years_in_business })}`
                : ""}
            </span>
          </div>
        </div>
      </div>

      {/* 2×2 action grid */}
      <div className="mb-4 grid grid-cols-2 gap-2.5">
        <Button
          leading="phone"
          size="md"
          disabled={!telUrl}
          onClick={telUrl ? () => window.open(telUrl, "_self") : undefined}
          data-testid="seller-action-call"
        >
          {t("cta.call")}
        </Button>
        <Button
          leading="msg"
          size="md"
          variant="secondary"
          disabled={!whatsappUrl}
          onClick={
            whatsappUrl
              ? () => window.open(whatsappUrl, "_blank", "noopener,noreferrer")
              : undefined
          }
          data-testid="seller-action-whatsapp"
        >
          {t("cta.whatsapp")}
        </Button>
        <Button
          leading="pin"
          size="md"
          variant="outline"
          disabled={!has2gis}
          onClick={
            geo2gisUrl
              ? () =>
                  window.open(geo2gisUrl, "_blank", "noopener,noreferrer")
              : undefined
          }
          data-testid="seller-action-2gis"
        >
          2GIS
        </Button>
        <Button
          leading="sparkles"
          size="md"
          variant="outline"
          onClick={() =>
            navigate(CHAT_ROUTE, {
              // A0: carry the seller as a chat seed so the assistant turn opens
              // with context (the composer prefills, no auto-send).
              state: { seed: t("seedAsk", { name: seller.business_name }) },
            })
          }
          data-testid="seller-action-assistant"
        >
          {t("cta.askAssistant")}
        </Button>
      </div>

      {/* Opening hours */}
      <div className="mb-3.5 flex items-center gap-2 rounded-[var(--g-radius)] bg-[var(--g-ground)] px-3.5 py-[11px]">
        <Icon name="clock" size={17} className="text-[var(--g-ink-3)]" />
        <span className="text-[13px] text-[var(--g-ink-2)]">
          {t("hours")}:{" "}
          <b className="tabular-nums text-foreground">
            {seller.operating_hours ?? t("hoursUnknown")}
          </b>
        </span>
        <span className="ml-auto truncate text-xs text-[var(--g-ink-3)]">
          {market}
        </span>
      </div>

      {/* Sample inventory */}
      <div className="mb-2.5 text-[13px] font-bold text-foreground">
        {t("inventory")}
      </div>

      {inventory.isError ? (
        <div
          role="alert"
          data-testid="seller-inventory-error"
          className="rounded-[var(--g-radius)] border border-border px-3 py-2.5 text-[13px] text-[var(--g-ink-3)]"
        >
          {t("inventoryError")}
        </div>
      ) : inventory.isPending ? (
        <div
          data-testid="seller-inventory-loading"
          className="flex flex-col gap-2"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 rounded-[var(--g-radius)] border border-border px-3 py-2.5"
            >
              <Skeleton w={32} h={32} r={10} />
              <Skeleton w="55%" h={13} />
              <Skeleton w={56} h={13} r={6} className="ml-auto" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="seller-inventory-empty"
          className="rounded-[var(--g-radius)] border border-border px-3 py-2.5 text-[13px] text-[var(--g-ink-3)]"
        >
          {t("inventoryEmpty")}
        </div>
      ) : (
        <div className="flex flex-col gap-2" data-testid="seller-inventory-list">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`seller-inventory-item-${item.id}`}
              className="flex items-center gap-2.5 rounded-[var(--g-radius)] border border-border px-3 py-2.5"
            >
              <div className="grid size-8 shrink-0 place-items-center rounded-[var(--g-radius)] bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)]">
                <Icon name="grid" size={16} />
              </div>
              <span className="flex-1 truncate text-[13px] font-medium text-foreground">
                {item.name}
              </span>
              <span className="shrink-0 text-[13.5px] font-bold tabular-nums text-foreground">
                {item.price_kgs !== null
                  ? fmtSom(parseDecimal(item.price_kgs), lang)
                  : t("noPrice")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
